import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../supabase';
import { NotificationService } from '../../services/notification/NotificationService';
import { isNativePlatform, getPlatformInfo } from '../../utils/platformUtils';
import { trackEvent, identifyUser as posthogIdentifyUser, resetUser as posthogResetUser } from '../analytics';
import { clearAllUserData } from '../../utils/userDataCleanup';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { languageManager } from '../../services/languageManager';
import { useSetAtom, useAtom } from 'jotai';
import { initializePushy } from '../../services/pushy';
import { fetchUserPreferencesAtom } from '../../atoms/v3/userPreferencesAtoms';
import { fetchSelectedCharacterAtom, syncSelectedCharacterAtom } from '../../atoms/v3/characterAtoms';
import { clearInviteCodeCacheAtom } from '../../atoms/v3/inviteCodeAtoms';
import { usePushyManager } from '../../hooks/usePushyManager';

// Import other analytics/monitoring tools
import Clarity from '@microsoft/clarity';
import { AnalyticsPlugin as FirebaseAnalytics } from '../firebase'; // Assuming FirebaseAnalytics is exported from your firebase.ts
import Hotjar from '@hotjar/browser';
import * as Sentry from "@sentry/react";
import { H as Highlight } from 'highlight.run'; // Added Highlight SDK for identify
import LogRocket from 'logrocket'; // Added LogRocket import

type AuthContextType = {
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  signOut: async () => {},
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // 获取邀请码缓存清理函数
  const clearInviteCodeCache = useSetAtom(clearInviteCodeCacheAtom);

  // 获取用户偏好设置
  const [, fetchUserPreferences] = useAtom(fetchUserPreferencesAtom);
  const [, fetchSelectedCharacter] = useAtom(fetchSelectedCharacterAtom);
  const [, syncSelectedCharacter] = useAtom(syncSelectedCharacterAtom);

  // Sync pushy manager with atoms
  usePushyManager();

  // Function to handle user identification across services
  const identifyAllServices = async (currentUser: User) => {
    if (!currentUser) return;
    console.log('[AuthProvider] Identifying user across services:', currentUser.id);

    const genericUserProps = {
      email: currentUser.email,
      // Add any other common user properties you want to track
    };

    // LogRocket
    try {
      LogRocket.identify(currentUser.id, {
        name: currentUser.user_metadata?.full_name || currentUser.email || `User-${currentUser.id}`, // Use full_name, then email, then ID
        email: currentUser.email || '', // Pass empty string if email is undefined
        // Add any other custom user variables for LogRocket here
        // Example: subscriptionType: currentUser.app_metadata?.subscription_type
      });
    } catch (error) {
      console.error('[AuthProvider] Error identifying user with LogRocket:', error);
    }

    // PostHog
    try {
      posthogIdentifyUser(currentUser.id, genericUserProps);
    } catch (error) {
      console.error('[AuthProvider] Error identifying user with PostHog:', error);
    }

    // 获取用户偏好设置（包括角色卡选择）
    try {
      console.log('[AuthProvider] Fetching user preferences for user:', currentUser.id);
      const preferences = await fetchUserPreferences();
      console.log('[AuthProvider] User preferences loaded successfully');

      // 加载用户选中的角色数据
      try {
        console.log('[AuthProvider] Syncing selected character data');
        await syncSelectedCharacter();
        console.log('[AuthProvider] Selected character synced successfully');
      } catch (characterError) {
        console.warn('[AuthProvider] Failed to sync selected character:', characterError);
        // 角色加载失败不应该影响登录流程，只记录警告
      }
    } catch (error) {
      console.warn('[AuthProvider] Failed to fetch user preferences:', error);
    }

    // Microsoft Clarity
    try {
      if (typeof Clarity !== 'undefined') {
        Clarity.identify(currentUser.id, undefined, undefined, currentUser.email || `User-${currentUser.id}`);
      }
    } catch (error) {
      console.error('[AuthProvider] Error identifying user with Clarity:', error);
    }

    // Firebase Analytics
    try {
      if (FirebaseAnalytics) {
        await FirebaseAnalytics.setUserId({ userId: currentUser.id });
        if (currentUser.email) {
          await FirebaseAnalytics.setUserProperty({ name: "email", value: currentUser.email });
        }
      }
    } catch (error) {
      console.error('[AuthProvider] Error setting user for Firebase Analytics:', error);
    }

    // Hotjar
    try {
      if (typeof Hotjar !== 'undefined') {
        const hotjarUserProps: { [key: string]: string | number | boolean | Date } = {};
        if (currentUser.email) {
          hotjarUserProps.email = currentUser.email;
        }
        if (Object.keys(hotjarUserProps).length > 0) {
            Hotjar.identify(currentUser.id, hotjarUserProps);
        }
      }
    } catch (error) {
      console.error('[AuthProvider] Error identifying user with Hotjar:', error);
    }

    // Sentry
    try {
      if (Sentry) {
        Sentry.setUser({ id: currentUser.id, ...genericUserProps });
      }
    } catch (error) {
      console.error('[AuthProvider] Error setting user for Sentry:', error);
    }

    // Highlight Frontend
    try {
      if (typeof Highlight !== 'undefined') {
        Highlight.identify(currentUser.email || `user-${currentUser.id}`, {
          id: currentUser.id,
        });
      }
    } catch (error) {
      console.error('[AuthProvider] Error identifying user with Highlight Frontend:', error);
    }
  };

  // Function to handle user reset across services
  const resetAllServices = async () => {
    console.log('[AuthProvider] Resetting user across services.');

    // PostHog
    try {
      posthogResetUser();
    } catch (error) {
      console.error('[AuthProvider] Error resetting user with PostHog:', error);
    }

    // Firebase Analytics
    try {
      if (FirebaseAnalytics) {
        await FirebaseAnalytics.reset();
        console.log('[AuthProvider] Firebase Analytics data reset.');
      }
    } catch (error) {
      console.error('[AuthProvider] Error resetting Firebase Analytics:', error);
    }

    // Sentry
    try {
      if (Sentry) {
        Sentry.setUser(null);
      }
    } catch (error) {
      console.error('[AuthProvider] Error resetting user for Sentry:', error);
    }
    // Clarity and Hotjar do not have explicit reset APIs; new sessions will be anonymous.

    // Highlight Frontend - no explicit reset, new sessions will be anonymous
    // You could call Highlight. αποnymize() if they provide such method, but usually not needed.

    // LogRocket - no explicit reset needed, new sessions will be anonymous automatically
  };

  useEffect(() => {
    console.log('[AuthProvider] useEffect triggered.');
    let initialSessionChecked = false;

    // 检查当前会话
    const checkSession = async () => {
      try {
        // 获取平台信息用于调试
        const platformInfo = getPlatformInfo();

        console.log(`[AuthProvider] Checking session on ${platformInfo.os}`, {
          isAndroid: platformInfo.isAndroid,
          isNative: platformInfo.isNative,
          userAgent: platformInfo.userAgent
        });

        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        // Android 平台特殊日志
        if (platformInfo.isAndroid) {
          console.log('[Android Auth] getSession result:', {
            hasSession: !!session,
            hasUser: !!session?.user,
            hasAccessToken: !!session?.access_token,
            tokenLength: session?.access_token?.length || 0,
            error: sessionError
          });
        }

        console.log('[AuthProvider] getSession result:', session);
        const currentUser = session?.user ?? null;
        setUser(currentUser);
        console.log('[AuthProvider] Initial user state set:', currentUser ? currentUser.id : 'null');

        if (currentUser) {
          identifyAllServices(currentUser); // Identify on initial session load
          // Delay pushy initialization to ensure auth state is stable
          setTimeout(() => initializePushy(), 1000);
        }
        initialSessionChecked = true;
      } catch (error) {
        console.error('[AuthProvider] Error getting session:', error);

        // Android 平台错误特殊处理
        const platformInfo = getPlatformInfo();
        if (platformInfo.isAndroid) {
          console.error('[Android Auth] Session check failed:', {
            error: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined,
            userAgent: platformInfo.userAgent
          });
        }
      } finally {
        // 确保即使出错也结束 loading
        if (!initialSessionChecked) { // 避免被 onAuthStateChange 覆盖
        setLoading(false);
        }
      }
    };

    checkSession();

    // 设置深度链接监听器（仅在原生平台）
    let appUrlOpenListener: any = null;
    let browserFinishedListener: any = null;

    if (isNativePlatform()) {
      console.log('[AuthProvider] Setting up deep link listener for native platform');

      appUrlOpenListener = App.addListener('appUrlOpen', async (event: { url: string }) => {
        console.log('[AuthProvider] Deep link received:', event.url);

        // 检查是否是认证回调 URL
        if (event.url.includes('auth/callback')) {
          console.log('[AuthProvider] Processing auth callback from deep link');

          try {
            // 解析 URL 获取认证信息
            const url = new URL(event.url);
            const fragment = url.hash || url.search;

            console.log('[AuthProvider] Auth callback fragment:', fragment);

            if (fragment) {
              // 手动解析 URL 中的认证信息
              const params = new URLSearchParams(fragment.startsWith('#') ? fragment.substring(1) : fragment);
              const accessToken = params.get('access_token');
              const refreshToken = params.get('refresh_token');

              if (accessToken) {
                console.log('[AuthProvider] Found access token in deep link');

                // 立即设置 loading 状态，给用户反馈
                setLoading(true);

                // 设置会话
                const { data, error } = await supabase.auth.setSession({
                  access_token: accessToken,
                  refresh_token: refreshToken || ''
                });

                if (error) {
                  console.error('[AuthProvider] Error setting session from deep link:', error);

                  // 认证失败时也关闭 in-app browser
                  try {
                    await Browser.close();
                    console.log('[AuthProvider] Closed in-app browser after auth failure');
                  } catch (browserError) {
                    console.warn('[AuthProvider] Failed to close in-app browser on auth failure:', browserError);
                  }

                  setLoading(false);
                  await trackEvent('login failed', {
                    method: 'social_deeplink',
                    error: error.message
                  });
                } else if (data.session) {
                  console.log('[AuthProvider] Auth callback successful, user:', data.session.user.id);

                  // 延迟关闭 in-app browser，让用户看到成功反馈
                  setTimeout(async () => {
                    try {
                      await Browser.close();
                      console.log('[AuthProvider] Successfully closed in-app browser after auth success');
                    } catch (browserError) {
                      // Browser.close() 可能在某些情况下失败，但不应该影响认证流程
                      console.warn('[AuthProvider] Failed to close in-app browser:', browserError);
                    }
                  }, 1000); // 1秒延迟

                  // 立即更新用户状态，不等待 onAuthStateChange
                  setUser(data.session.user);
                  setLoading(false);

                  // 记录登录成功事件
                  await trackEvent('user logged in', {
                    method: 'social_deeplink',
                    user_id: data.session.user.id
                  });

                  // 识别用户
                  await posthogIdentifyUser(data.session.user.id, {
                    email: data.session.user.email,
                    auth_provider: 'social_deeplink'
                  });

                  // 执行其他登录后的操作
                  identifyAllServices(data.session.user);
                  setTimeout(() => {
                    attemptDeviceRegistration(data.session.user);
                  }, 100);
                }
              }
            }
          } catch (err) {
            console.error('[AuthProvider] Error handling deep link auth callback:', err);

            // 发生异常时也尝试关闭 in-app browser
            try {
              await Browser.close();
              console.log('[AuthProvider] Closed in-app browser after auth error');
            } catch (browserError) {
              console.warn('[AuthProvider] Failed to close in-app browser on auth error:', browserError);
            }

            await trackEvent('login error', {
              method: 'social_deeplink',
              error: err instanceof Error ? err.message : String(err)
            });
          }
        }
      });

      // 设置 Browser 事件监听器 - 监听用户关闭 in-app browser
      console.log('[AuthProvider] Setting up browser event listener for native platform');

      browserFinishedListener = Browser.addListener('browserFinished', () => {
        console.log('[AuthProvider] Browser finished - user closed in-app browser');
        // 用户关闭了 in-app browser，可能是取消了认证
        // 这里可以记录取消事件，但不需要特殊处理，因为如果认证成功会通过深度链接回调
        trackEvent('social login cancelled', {
          method: 'in_app_browser_closed'
        });
      });
    }

    // 监听认证状态变化
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event: any, session) => {
      console.log('[AuthProvider] onAuthStateChange event:', _event);
      console.log('[AuthProvider] onAuthStateChange session:', session ? `User ID: ${session.user.id}` : 'null');
      const currentUser = session?.user ?? null;
      if (_event === 'SIGNED_OUT' || !session) {
        setUser(null); // 强制清空
        setLoading(false);
        resetAllServices(); // Call reset for all services

        // 使用专门的清理工具进行全面的用户数据清理
        clearAllUserData();

        // 清理邀请码验证缓存
        clearInviteCodeCache();

        return;
      }
      setUser(currentUser); // 其他事件正常更新
      setLoading(false);

      if (_event === 'SIGNED_IN' && currentUser) {
        console.log('[AuthProvider] User signed in, identifying and attempting device registration for user:', currentUser.id);
        identifyAllServices(currentUser);
        // Ensure user has valid ID before initializing pushy
        if (currentUser.id) {
          initializePushy();
        }
      } else if (_event === 'SIGNED_OUT') {
        console.log('[AuthProvider] User signed out, resetting services.');
        resetAllServices();

        // 处理用户登出时的语言设置
        try {
          languageManager.handleUserLogout();
        } catch (error) {
          console.warn('[AuthProvider] Failed to handle language preferences on logout:', error);
        }

        // Consider unregistering device token here if implemented
      } else if (_event === 'TOKEN_REFRESHED' && currentUser) {
        console.log('[AuthProvider] Token refreshed for user:', currentUser.id);
        // Optionally re-identify or update properties if they might change with token refresh
        // identifyAllServices(currentUser);
      } else if (_event === 'USER_UPDATED' && currentUser) {
        console.log('[AuthProvider] User updated:', currentUser.id);
        identifyAllServices(currentUser); // Re-identify with updated properties
      }
    });

    return () => {
      console.log('[AuthProvider] Cleanup: unsubscribing from auth state changes');
      subscription.unsubscribe();

      // 清理深度链接监听器
      if (appUrlOpenListener) {
        console.log('[AuthProvider] Cleanup: removing deep link listener');
        appUrlOpenListener.remove();
      }

      // 清理 Browser 事件监听器
      if (browserFinishedListener) {
        console.log('[AuthProvider] Cleanup: removing browser event listener');
        browserFinishedListener.remove();
      }
    };
  }, []); // 移除 attemptDeviceRegistration

  const signOut = async () => {
    console.log('[AuthProvider] Signing out user');
    const currentUserId = user?.id;

    if (currentUserId) {
      trackEvent('user logged out', {
        user_id: currentUserId
      });
    }

    try {
      await supabase.auth.signOut();
      console.log('[AuthProvider] Supabase signOut successful');
      resetAllServices(); // Call reset for all services

      // 处理用户登出时的语言设置
      try {
        languageManager.handleUserLogout();
      } catch (error) {
        console.warn('[AuthProvider] Failed to handle language preferences on logout:', error);
      }
    } catch (error) {
      console.error('[AuthProvider] Error signing out from Supabase:', error);
      // Even if Supabase signOut fails, attempt to clear local state and reset analytics users
      setUser(null); // Force clear local user state
      resetAllServices();

      // 即使登出失败也要处理语言设置
      try {
        languageManager.handleUserLogout();
      } catch (langError) {
        console.warn('[AuthProvider] Failed to handle language preferences on logout error:', langError);
      }

      trackEvent('logout error (api_fail)', {
        error: error instanceof Error ? error.message : String(error)
      });
    } finally {
        // 使用专门的清理工具进行全面的用户数据清理
        clearAllUserData();

        // 清理邀请码验证缓存
        clearInviteCodeCache();
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);